import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import EvaluationForm from "@/components/EvaluationForm";
import EvaluationCard from "@/components/EvaluationCard";
import PlayerMetricsSection from "@/components/PlayerMetricsSection";
import PlayerMediaManager from "@/components/PlayerMediaManager";

export default function EvaluatorPlayerView() {
  const { playerId } = useParams();
  const { user, isEvaluator } = useAuth();
  const [player, setPlayer] = useState(null);
  const [evals, setEvals] = useState([]);
  const [camps, setCamps] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    const { data: p } = await api.get(`/players/${playerId}`);
    setPlayer(p);
    const { data: ev } = await api.get("/evaluations", { params: { player_id: p.id } });
    setEvals(ev.items || []);
    const { data: c } = await api.get("/camps");
    setCamps(c.items || []);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [playerId]);

  if (!player) {
    return <PublicLayout><div className="max-w-3xl mx-auto p-10 label-overline">Loading player…</div></PublicLayout>;
  }

  if (!isEvaluator) {
    return (
      <PublicLayout>
        <div className="max-w-3xl mx-auto p-10">
          <div className="font-display text-3xl uppercase">Access Restricted</div>
          <p className="text-sm text-slate-600 mt-2">Evaluation tools are available to CBR evaluators and admins only.</p>
          <Link to={`/prospects/${player.slug}`} className="text-[#E63946] text-sm underline mt-3 inline-block">View public profile →</Link>
        </div>
      </PublicLayout>
    );
  }

  const myDrafts = evals.filter((e) => e.evaluator_user_id === user.id && e.status === "draft");

  return (
    <PublicLayout>
      <section className="bg-[#0A192F] text-white py-10 border-b-4 border-[#E63946]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 flex items-center gap-5 flex-wrap">
          <div className="w-20 h-20 bg-slate-700 border-2 border-white overflow-hidden grid place-items-center">
            {player.profile_photo_url ? (
              <img src={player.profile_photo_url} alt={player.first_name} className="w-full h-full object-cover" />
            ) : (
              <span className="font-display text-2xl">{player.first_name?.[0]}{player.last_name?.[0]}</span>
            )}
          </div>
          <div className="flex-1">
            <div className="label-overline text-[#E63946]">Evaluator View</div>
            <h1 data-testid="evaluator-player-name" className="font-display text-4xl uppercase tracking-tighter mt-1">
              {player.first_name} {player.last_name}
            </h1>
            <div className="text-sm text-white/70 mt-1 font-data">
              Class of {player.graduation_year || "—"} · {player.primary_position || "—"} · {player.bats || "?"}/{player.throws || "?"} · {player.city || ""}, {player.province || ""}
            </div>
          </div>
          <button
            data-testid="start-evaluation-button"
            onClick={() => setShowForm(true)}
            className="bg-[#E63946] hover:bg-[#D90429] px-6 py-3 text-sm font-semibold uppercase tracking-wider rounded-sm"
          >
            New Evaluation
          </button>
        </div>
      </section>

      {showForm && (
        <section className="bg-white py-8">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <EvaluationForm
              playerId={player.id}
              playerPosition={player.primary_position}
              camps={camps}
              onSaved={async () => { setShowForm(false); await load(); }}
            />
          </div>
        </section>
      )}

      <section className="bg-[#F8F9FA] py-10">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <h2 className="font-display text-2xl uppercase tracking-tight">Submitted Reports</h2>
            {evals.filter((e) => e.status === "submitted").length === 0 ? (
              <div className="bg-white border border-dashed border-slate-300 p-8 text-sm text-slate-500 text-center">
                No submitted evaluations for this player yet.
              </div>
            ) : (
              evals.filter((e) => e.status === "submitted").map((e) => <EvaluationCard key={e.id} evaluation={e} />)
            )}

            {myDrafts.length > 0 && (
              <>
                <h2 className="font-display text-2xl uppercase tracking-tight mt-6">Your Drafts</h2>
                {myDrafts.map((e) => <EvaluationCard key={e.id} evaluation={e} hideEvaluator />)}
              </>
            )}
          </div>

          <aside className="space-y-4">
            <PlayerMetricsSection player={player} />
            <PlayerMediaManager playerId={player.id} mode="evaluator" />
          </aside>
        </div>
      </section>
    </PublicLayout>
  );
}
